import type { AssistantMessage } from "../types.js";

/**
 * Regex patterns to detect context overflow errors from different providers.
 *
 * These patterns match error messages returned when the input exceeds
 * the model's context window.
 *
 * Provider-specific patterns (with example error messages):
 *
 * - Anthropic: "prompt is too long: 213462 tokens > 200000 maximum"
 * - OpenAI: "Your input exceeds the context window of this model"
 * - Google: "The input token count (1196265) exceeds the maximum number of tokens allowed (1048575)"
 * - xAI: "This model's maximum prompt length is 131072 but the request contains 537812 tokens"
 * - Groq: "Please reduce the length of the messages or completion"
 * - OpenRouter: "This endpoint's maximum context length is X tokens. However, you requested about Y tokens"
 * - llama.cpp: "the request exceeds the available context size, try increasing it"
 * - LM Studio: "tokens to keep from the initial prompt is greater than the context length"
 * - GitHub Copilot: "prompt token count of X exceeds the limit of Y"
 * - MiniMax: "invalid params, context window exceeds limit"
 * - Kimi For Coding: "Your request exceeded model token limit: X (requested: Y)"
 * - Cerebras: Returns "400/413 status code (no body)" - handled separately below
 * - Mistral: "Prompt contains X tokens ... too large for model with Y maximum context length"
 * - z.ai: May accept overflow silently - handled via full input usage > contextWindow
 * - Ollama: Silently truncates input - not detectable via error message
 */
const LENGTH_RETRY_EXHAUSTED_PATTERN =
	/^Response truncated at (?:the configured output token limit|token limit) after \d+ attempts?(?: — output exceeded the model's maximum token budget)?(?:\nProvider detail: .+)?$/is;

export interface ContextOverflowOptions {
	/** Maximum output tokens requested for the exhausted response. */
	maxOutputTokens?: number;
	/** Estimated request-input tokens when the provider omitted usage. */
	estimatedInputTokens?: number;
}

/** Whether the agent exhausted its bounded retries at the configured output limit. */
export function isOutputLimitExhaustion(message: AssistantMessage): boolean {
	return (
		message.stopReason === "error" &&
		typeof message.errorMessage === "string" &&
		LENGTH_RETRY_EXHAUSTED_PATTERN.test(message.errorMessage)
	);
}

const OVERFLOW_PATTERNS = [
	/prompt is too long/i, // Anthropic
	/input is too long for requested model/i, // Amazon Bedrock
	/exceeds the context window/i, // OpenAI (Completions & Responses API)
	/input token count.*exceeds the maximum/i, // Google (Gemini)
	/maximum prompt length is \d+/i, // xAI (Grok)
	/reduce the length of the messages/i, // Groq
	/maximum context length is \d+ tokens/i, // OpenRouter (all backends)
	/exceeds the limit of \d+/i, // GitHub Copilot
	/exceeds the available context size/i, // llama.cpp server
	/greater than the context length/i, // LM Studio
	/context window exceeds limit/i, // MiniMax
	/exceeded model token limit/i, // Kimi For Coding
	/too large for model with \d+ maximum context length/i, // Mistral
	/model_context_window_exceeded/i, // z.ai non-standard finish_reason surfaced as error text
	/context[_ ]length[_ ]exceeded/i, // Generic fallback
	/too many tokens/i, // Generic fallback
	/token limit exceeded/i, // Generic fallback
];

/**
 * Check if an assistant message represents a context overflow error.
 *
 * This handles three cases:
 * 1. Error-based overflow: Most providers return stopReason "error" with a
 *    specific error message pattern.
 * 2. Context-constrained truncation: dreb exhausted its length retries and the
 *    provider signal or input-plus-configured-output budget indicates that the
 *    response may not have fit in the remaining context window.
 * 3. Silent overflow: Some providers accept requests beyond the configured window
 *    and return successfully (the server's hard limit may be higher). For these,
 *    we compare input + cacheRead + cacheWrite against the configured window;
 *    output tokens are not part of the input size.
 *
 * ## Reliability by Provider
 *
 * **Reliable detection (returns error with detectable message):**
 * - Anthropic: "prompt is too long: X tokens > Y maximum"
 * - OpenAI (Completions & Responses): "exceeds the context window"
 * - Google Gemini: "input token count exceeds the maximum"
 * - xAI (Grok): "maximum prompt length is X but request contains Y"
 * - Groq: "reduce the length of the messages"
 * - Cerebras: 400/413 status code (no body)
 * - Mistral: "Prompt contains X tokens ... too large for model with Y maximum context length"
 * - OpenRouter (all backends): "maximum context length is X tokens"
 * - llama.cpp: "exceeds the available context size"
 * - LM Studio: "greater than the context length"
 * - Kimi For Coding: "exceeded model token limit: X (requested: Y)"
 *
 * **Unreliable detection:**
 * - z.ai: Sometimes accepts overflow silently, sometimes returns rate limit errors.
 * - GitHub Copilot: May accept input beyond a conservative configured window.
 *   Pass contextWindow to detect these cases using input + cacheRead + cacheWrite.
 * - Ollama: Silently truncates input without error. Cannot be detected via this function.
 *   The response will have usage.input < expected, but we don't know the expected value.
 *
 * ## Custom Providers
 *
 * If you've added custom models via settings.json, this function may not detect
 * overflow errors from those providers. To add support:
 *
 * 1. Send a request that exceeds the model's context window
 * 2. Check the errorMessage in the response
 * 3. Create a regex pattern that matches the error
 * 4. The pattern should be added to OVERFLOW_PATTERNS in this file, or
 *    check the errorMessage yourself before calling this function
 *
 * @param message - The assistant message to check
 * @param contextWindow - Optional configured window for usage-based overflow detection
 * @param options - Output limit and input estimate used only after bounded length retries are exhausted
 * @returns true if the message indicates a context overflow
 */
export function isContextOverflow(
	message: AssistantMessage,
	contextWindow?: number,
	options: ContextOverflowOptions = {},
): boolean {
	// Case 1: Check error message patterns
	if (message.stopReason === "error" && message.errorMessage) {
		// Check known patterns
		if (OVERFLOW_PATTERNS.some((p) => p.test(message.errorMessage!))) {
			return true;
		}

		// Cerebras returns 400/413 with no body for context overflow
		// Note: 429 is rate limiting (requests/tokens per time), NOT context overflow
		if (/^4(00|13)\s*(status code)?\s*\(no body\)/i.test(message.errorMessage)) {
			return true;
		}

		// A provider can report finish_reason "length" when the response ran out of
		// context rather than output allowance. dreb turns an exhausted retry sequence
		// into this error. Classify it as overflow only when actual usage reaches the
		// context ceiling or the configured output could not fit beside the request.
		if (contextWindow && isOutputLimitExhaustion(message)) {
			const usageTokens =
				message.usage.totalTokens ||
				message.usage.input + message.usage.output + message.usage.cacheRead + message.usage.cacheWrite;
			if (usageTokens >= contextWindow) {
				return true;
			}

			const recordedInput = message.usage.input + message.usage.cacheRead + message.usage.cacheWrite;
			const inputTokens = recordedInput || options.estimatedInputTokens || 0;
			if (options.maxOutputTokens && inputTokens + options.maxOutputTokens > contextWindow) {
				return true;
			}
		}
	}

	// Case 3: Successful response whose full input usage exceeds the configured window.
	// Anthropic-style usage reports cache creation separately from uncached input.
	if (contextWindow && message.stopReason === "stop") {
		const inputTokens = message.usage.input + message.usage.cacheRead + message.usage.cacheWrite;
		if (inputTokens > contextWindow) {
			return true;
		}
	}

	return false;
}

/**
 * Get the overflow patterns for testing purposes.
 */
export function getOverflowPatterns(): RegExp[] {
	return [...OVERFLOW_PATTERNS];
}
